function isPreOrderVariant(variant) {
    if (!variant || variant.available !== true) return false;
    return variant.inventory_management === "shopify" && variant.inventory_policy === "continue" && variant.inventory_quantity <= 0;
}

class ProductStickyATC extends HTMLElement {
    constructor() {
        super();

        this.props = {
            product: {},
            selectedVariant: {},
            variants: [],
            options: [],
        };

        this.state = {
            quantity: 1,
            drawerStatus: "idle", // idle | active
            addToCartStatus: "idle", // loading | idle
            product: {},
            selectedVariant: {},
            isVariantDropdownOpen: false,
            isStuck: false,
        };
    }

    connectedCallback() {
        this.initState();
        this.initProps();

        this.render(this.getRenderData());

        setTimeout(() => {
            this.initEventListeners();
        }, 100);

        document.addEventListener("click", (event) => {
            if (!this.state.isVariantDropdownOpen) return;
            if (!this.contains(event.target)) {
                this.updateState({ isVariantDropdownOpen: false });
            }
        });

        this.observeStickyState();

        ProductPageStore.subscribe((event, prevState) => {
            const { currentProduct, currentVariant } = event.detail;
            this.updateState({
                product: currentProduct,
                selectedVariant: currentVariant,
            });
        });
    }

    initState() {
        const { currentProduct, currentVariant } = ProductPageStore.getState();
        this.state.product = currentProduct;
        this.state.options = currentProduct.options;
        this.state.selectedVariant = currentVariant;
    }

    initProps() {
        this.props.buttonText = this.dataset.buttonText || "Add To Cart";
        this.props.vatText = this.dataset.vatText || "incl. VAT";
        this.props.preOrderText = this.dataset.preOrderText || "Pre-order";
    }

    observeStickyState() {
        // <product-sticky-atc> is always rendered as a direct child of the #pdp-top-price bar
        // (see theme/snippets/block-price-on-top-pdp.liquid), so this.parentElement finds it
        // without depending on that literal id. document.getElementById("pdp-top-price") broke in
        // Storybook, where LiquidRenderer suffixes every id to keep multiple story instances on
        // one page from colliding (components/LiquidRenderer.tsx's scopeIdsToInstance) — that
        // rewrite only touches inline <script> markup, not this external asset file, so the
        // lookup silently found nothing and the sticky bar never activated there.
        const priceBar = this.parentElement;
        if (!priceBar) return;

        const getNaturalTop = () => priceBar.getBoundingClientRect().top + window.scrollY;

        this.priceBarInitialTop = getNaturalTop();

        window.addEventListener(
            "scroll",
            () => {
                if (!this.state.isStuck) {
                    this.priceBarInitialTop = getNaturalTop();
                }
                this.setStuck(window.scrollY >= this.priceBarInitialTop);
            },
            { passive: true },
        );
    }

    setStuck(isStuck) {
        if (isStuck === this.state.isStuck) return;

        // See the comment in observeStickyState() for why these aren't document.getElementById.
        // The placeholder is #pdp-top-price's previous sibling in the same snippet.
        const priceBar = this.parentElement;
        const placeholder = priceBar?.previousElementSibling;

        if (priceBar) {
            if (isStuck) {
                if (placeholder) {
                    placeholder.classList.remove("hidden");
                    placeholder.classList.add("!flex");
                }
                priceBar.classList.add("fixed", "top-0", "left-0", "right-0", "py-6", "px-4", "pdp-top-price-shadow");
            } else {
                priceBar.classList.remove("fixed", "top-0", "left-0", "right-0", "py-6", "px-4", "pdp-top-price-shadow");
                if (placeholder) {
                    placeholder.classList.add("hidden");
                    placeholder.classList.remove("!flex");
                    placeholder.style.height = "";
                }
            }
        }

        this.updateState({ isStuck });
    }

    updateState(updates) {
        this.state = { ...this.state, ...updates };
        this.render(this.getRenderData());
        this.initEventListeners();
    }

    pollingMainCTA() {
        const interval = setInterval(() => {
            const mainCTA = this.closest("section").querySelector("block-buy-button");
            const isLoading = mainCTA.submitButton.classList.contains("loading");
            if (!isLoading) {
                this.updateState({ addToCartStatus: "idle" });
                clearInterval(interval);
            }
        }, 100);
    }

    initEventListeners() {
        const mainCTA = document.getElementById("add-to-cart-cta");
        const CTAButtons = document.querySelectorAll(".drawer-CTA-add");

        CTAButtons.forEach((element) => {
            element.addEventListener("click", (event) => {
                event.preventDefault();

                const buttons = [...mainCTA.querySelectorAll("button")]; // Assuming the form is a parent of mainCTA
                const button = buttons.find((button) => button.getBoundingClientRect().width > 0);
                button?.click();

                this.updateState({ addToCartStatus: "loading" });
                this.pollingMainCTA();
            });
        });

        const dropdownTrigger = this.querySelector("#variant-dropdown-trigger");
        if (dropdownTrigger) {
            dropdownTrigger.addEventListener("click", (event) => {
                event.preventDefault();
                event.stopPropagation();
                this.toggleVariantDropdown();
            });
        }

        const dropdownOptions = this.querySelectorAll("#variant-dropdown-list [data-variant-id]");
        dropdownOptions.forEach((option) => {
            option.addEventListener("click", (event) => {
                event.preventDefault();
                this.handleVariantClick(event);
            });
        });
    }

    toggleVariantDropdown() {
        this.updateState({ isVariantDropdownOpen: !this.state.isVariantDropdownOpen });
    }

    getRenderData() {
        const result = {};
        result.isAddingToCart = this.state.addToCartStatus === "loading";
        result.isExpand = this.state.drawerStatus === "active";
        result.title = this.state.product.title;
        result.size = this.state.selectedVariant?.title?.split("|")?.[0]?.trim();
        result.isVariantDropdownOpen = this.state.isVariantDropdownOpen;
        result.isStuck = this.state.isStuck;
        result.buttonText = this.props.buttonText;
        result.isPreOrder = isPreOrderVariant(this.state.selectedVariant);
        return result;
    }

    handleVariantClick(event) {
        const option = event.currentTarget;
        if (option.disabled) return;

        // data-*-attrs are always strings; mock variant ids are strings (e.g. "A-CALKING")
        // while Shopify ids are numbers. Number("A-CALKING") is NaN and breaks the lookup,
        // so compare via String() on both sides.
        const clickedVariantId = option.dataset.variantId;
        const clickedVariant = this.state.product.variants.find((variant) => String(variant.id) === String(clickedVariantId));

        if (clickedVariant) {
            ProductPageStore.setState({ currentVariant: clickedVariant });
        }

        this.updateState({ isVariantDropdownOpen: false });
    }

    renderVariantOption(variant) {
        if (!variant.available) return "";

        const currencyUnit = "₱";
        const vatText = this.props.vatText;
        const { selectedVariant } = this.state;
        const isSelected = variant.id === selectedVariant?.id;
        const price = (variant.price / 100).toLocaleString();

        return `
      <button type="button"
        class="cursor-pointer w-full flex items-center gap-2 px-4 py-2 text-sm text-left h-[55px] border-none bg-[var(--color-neutral-0)] hover:bg-[#e9e3df]"
        data-variant-id="${variant.id}"
        role="option"
        aria-selected="${isSelected}"
      >
        <span class="shrink-0 w-4 ${isSelected ? "" : "hidden"}">
       <svg
  width="16"
  height="16"
  viewBox="0 0 16 16"
  fill="none"
  xmlns="http://www.w3.org/2000/svg"
>
  <path
    d="M1.5 8.5L5.5 13.5L14.5 2.5"
    stroke="#403833"
    stroke-width="1.5"
    stroke-linecap="round"
    stroke-linejoin="round"
  />
</svg>

          
        </span>
        <span class="flex-1 truncate">${variant.title}</span>
        <span class="flex flex-col items-end shrink-0 leading-[1.5]">
          <span class="font-semibold">${currencyUnit}${price}</span>
          <span class="text-xs text-[var(--color-neutral-700)]">${vatText}</span>
        </span>
      </button>
    `;
    }

    renderSelectionList(data) {
        const { selectedVariant, product } = this.state;
        if (!product || !selectedVariant || selectedVariant.title == "Default Title" || !product.variants || product.variants.length <= 1) {
            return "";
        }

        const isOpen = data.isVariantDropdownOpen;

        return `
        <div class="relative">
          <button type="button"
            id="variant-dropdown-trigger"
            class="cursor-pointer w-full flex items-center justify-between gap-2 bg-[var(--color-neutral-50)] h-[48px] px-4 py-3 text-sm font-normal border-none  ${
                isOpen ? "rounded-lg rounded-bl-none rounded-br-none" : "rounded-lg"
            }"
            aria-haspopup="listbox"
            aria-expanded="${isOpen}"
          >
            <span class="truncate">${selectedVariant.title}</span>
            <svg class="shrink-0 transition-transform duration-200 ${
                isOpen ? "rotate-180" : ""
            }" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 17 9" fill="none">
              <path d="M16.0669 0.183106C15.8228 -0.0610354 15.4272 -0.0610354 15.1831 0.183106L8.12501 7.24121L1.0669 0.183106C0.822756 -0.0610354 0.427248 -0.0610354 0.183106 0.183106C-0.0610354 0.427248 -0.0610354 0.822756 0.183106 1.0669L7.68311 8.5669C7.80518 8.68896 7.9651 8.75 8.12501 8.75C8.28532 8.75 8.44483 8.68896 8.5669 8.56689L16.0669 1.06689C16.311 0.822748 16.311 0.427248 16.0669 0.183106Z" fill="#403833"/>
            </svg>
          </button>
          <div
            id="variant-dropdown-list"
            role="listbox"
            class="absolute left-0 right-0 top-full z-10 bg-[var(--color-neutral-0)] rounded-lg shadow-[0_6px_25px_2px_#00000014] overflow-hidden ${
                isOpen ? "pt-1 rounded-lg rounded-tl-none rounded-tr-none border-t-[1px] border-[var(--color-neutral-500)]" : "rounded-lg hidden"
            }"
          >
            ${product.variants.map((variant) => this.renderVariantOption(variant)).join("")}
          </div>
        </div>
    `;
    }

    render(data) {
        const isVisible = data.isStuck;
        const buttonDisplayClass = isVisible ? "flex" : "hidden";
        const dropdownDisplayClass = isVisible ? "block" : "hidden";
        const buttonOpacityClass = data.isAddingToCart ? "opacity-50" : "opacity-100";
        const buttonColorClass = data.isPreOrder
            ? "bg-[var(--color-neutral-900)] text-[var(--color-neutral-0)]"
            : "bg-[var(--color-primary-400)] text-[var(--color-neutral-900)]";
        const buttonLabel = data.isPreOrder ? this.props.preOrderText : data.buttonText;

        this.innerHTML = `
    <button class="drawer-CTA-add shrink-0 h-9 cursor-pointer ${buttonColorClass} ${buttonDisplayClass} items-center justify-center rounded-[32px] gap-2 py-2 px-4 leading-none ${buttonOpacityClass} ${
        data.isAddingToCart ? "cursor-not-allowed" : ""
    }" ${data.isAddingToCart ? "disabled" : ""}>

      <p class="[text-box-trim:trim-both] inline-block font-bold text-sm leading-none whitespace-nowrap ${data.isAddingToCart ? "hidden" : ""}">
      ${buttonLabel}
      </p>
      <div class="loading-overlay__spinner w-4 ${data.isAddingToCart ? "" : "hidden"}">
        <svg
          aria-hidden="true"
          focusable="false"
          role="presentation"
          class="spinner"
          viewBox="0 0 66 66"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle style="stroke: white;" class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
        </svg>
      </div>
    </button>
    <div class="w-full ${dropdownDisplayClass}">
      ${this.renderSelectionList(data)}
    </div>
    `;
    }
}

customElements.define("product-sticky-atc", ProductStickyATC);
